import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  UserCog,
  Building2,
  Users,
  Shield,
  Mail,
  Copy,
  CheckCircle2,
  Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const testUsers = [
  {
    role: 'admin',
    email: 'oldoak@gmail.com',
    name: 'Admin User',
    description: 'Full system access - manage employers, employees, benefits, categories',
    badgeColor: 'bg-purple-100 text-purple-700',
    icon: Shield,
    status: 'Current User'
  },
  {
    role: 'employer_hr',
    email: 'hr@marcom.com',
    name: 'HR Manager',
    description: 'Manage employees, view enrollments, export census data',
    badgeColor: 'bg-amber-100 text-amber-700',
    icon: Building2,
    employer: 'Marcom Industries',
    status: 'Needs Invitation'
  },
  {
    role: 'employee',
    email: 'employee@marcom.com',
    name: 'John Employee',
    description: 'Browse benefits, enroll in coverage, view profile - has NOT seen welcome screen',
    badgeColor: 'bg-emerald-100 text-emerald-700',
    icon: Users,
    employer: 'Marcom Industries',
    status: 'Needs Invitation'
  },
  {
    role: 'employee',
    email: 'jane.smith@marcom.com',
    name: 'Jane Smith',
    description: 'Browse benefits, enroll in coverage - has confirmed profile',
    badgeColor: 'bg-emerald-100 text-emerald-700',
    icon: Users,
    employer: 'Marcom Industries',
    status: 'Needs Invitation'
  }
];

export default function SetupTestUsers() {
  const copyEmail = (email) => {
    navigator.clipboard.writeText(email);
    toast.success('Email copied to clipboard');
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8 p-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Test User Setup</h1>
        <p className="text-slate-500">
          Set up test users to explore different roles in the benefits system
        </p>
      </div>

      {/* Instructions */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="w-4 h-4 text-blue-600" />
        <AlertDescription className="text-blue-900">
          <p className="font-medium mb-2">How to set up test users:</p>
          <ol className="list-decimal ml-4 space-y-1 text-sm">
            <li>You're already logged in as the Admin user (oldoak@gmail.com)</li>
            <li>To test other roles, invite the users below through your dashboard settings</li>
            <li>Each user will receive an invitation email to set their password</li>
            <li>Once invited, they'll be automatically assigned the correct role and linked to the employee profile</li>
            <li>Open the app in a different browser/incognito window to test multiple roles simultaneously</li>
          </ol>
        </AlertDescription>
      </Alert>

      {/* Current Status */}
      <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
        <CardContent className="p-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="font-semibold text-emerald-900">You're logged in as Admin</p>
            <p className="text-sm text-emerald-700">
              Your account (oldoak@gmail.com) has been configured with admin role and employee profile
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Test Users */}
      <div className="grid gap-4">
        {testUsers.map((user, idx) => {
          const Icon = user.icon;
          const isCurrentUser = user.status === 'Current User';
          
          return (
            <Card key={idx} className={isCurrentUser ? 'border-emerald-300 bg-emerald-50/50' : ''}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg mb-1">{user.name}</CardTitle>
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <Badge className={user.badgeColor}>
                          {user.role.replace('_', ' ').toUpperCase()}
                        </Badge>
                        {user.employer && (
                          <Badge variant="outline">
                            <Building2 className="w-3 h-3 mr-1" />
                            {user.employer}
                          </Badge>
                        )}
                        {isCurrentUser ? (
                          <Badge className="bg-emerald-500 text-white">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-600 border-amber-300">
                            {user.status}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">{user.description}</p>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <code className="text-sm font-mono text-slate-700">{user.email}</code>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyEmail(user.email)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Next Steps */}
      <Card className="bg-gradient-to-r from-slate-50 to-slate-100">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCog className="w-5 h-5 text-slate-700" />
            Next Steps
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
              1
            </div>
            <div>
              <p className="font-medium text-slate-900">Invite Test Users</p>
              <p className="text-slate-600">Go to your Base44 dashboard â†’ Settings â†’ Invite Users</p>
              <p className="text-slate-600 mt-1">Invite: hr@marcom.com, employee@marcom.com, jane.smith@marcom.com</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
              2
            </div>
            <div>
              <p className="font-medium text-slate-900">Set User Roles</p>
              <p className="text-slate-600">After invitation, go to Sub-Admin Management page to set roles:</p>
              <p className="text-slate-600 mt-1">â€¢ Set hr@marcom.com â†’ employer_hr role</p>
              <p className="text-slate-600">â€¢ Set employee@marcom.com â†’ employee role</p>
              <p className="text-slate-600">â€¢ Set jane.smith@marcom.com â†’ employee role</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
              3
            </div>
            <div>
              <p className="font-medium text-slate-900">Test Each Role</p>
              <p className="text-slate-600">Use different browsers/incognito windows to log in as each user and test their dashboards</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Role Capabilities Quick Reference</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <p className="font-medium text-purple-700">Admin / Sub-Admin</p>
              <ul className="text-slate-600 space-y-1 ml-4">
                <li>â€¢ Full system access</li>
                <li>â€¢ Manage all employers & employees</li>
                <li>â€¢ Configure benefits & categories</li>
                <li>â€¢ View analytics & reports</li>
                <li>â€¢ Upload census data</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <p className="font-medium text-amber-700">Employer (HR)</p>
              <ul className="text-slate-600 space-y-1 ml-4">
                <li>â€¢ View their company's employees</li>
                <li>â€¢ Track enrollments</li>
                <li>â€¢ Export census data</li>
                <li>â€¢ Send reminder emails</li>
              </ul>
            </div>
            
            <div className="space-y-2 md:col-span-2">
              <p className="font-medium text-emerald-700">Employee</p>
              <ul className="text-slate-600 space-y-1 ml-4">
                <li>â€¢ View and edit personal profile</li>
                <li>â€¢ Browse benefits catalog</li>
                <li>â€¢ Enroll in benefits</li>
                <li>â€¢ View payroll deductions</li>
                <li>â€¢ Access Victoria AI assistant</li>
                <li>â€¢ Schedule calls with benefits specialists</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}